import {
  memo,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type SetStateAction,
} from "react";

type Language = "en" | "kh";
type GuestStatus = "Joining" | "Maybe" | "Not yet";
type GuestGroup = "ភ្ញៀវកូនស្រី" | "ភ្ញៀវកូនប្រុស";
type UploadedDraftGroupFilter = "all" | "bride" | "groom";
type ImportGuestGroup = "bride" | "groom";
type SameNameGiftFilter = "all" | "with_gift" | "no_gift";
const REGISTRY_ROWS_PER_PAGE = 150;

const API_BASE =
  process.env.NEXT_PUBLIC_WEDDING_API_BASE?.trim().replace(/\/$/, "") || "/api";
const API_IMPORT_TEMPLATE_PATH = "/wedding/registry/drafts/import-excel/template";
const API_IMPORT_EXCEL_PATH = "/wedding/registry/drafts/import-excel";
const buildApiUrl = (path: string) =>
  `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

type Guest = {
  id: number;
  name: string;
  phone: string;
  status: GuestStatus;
  group: GuestGroup;
  category: string;
  notes: string;
  latitude: number | null;
  longitude: number | null;
  giftAmount?: number | null;
  giftCurrency?: "USD" | "Riel";
  source?: "api" | "local";
  invited?: boolean;
  done?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type RegistryText = {
  title: string;
  registered: string;
  searchPlaceholder: string;
  table: {
    no: string;
    name: string;
    contact: string;
    category: string;
    status: string;
    notes: string;
    latitude: string;
    longitude: string;
    locate: string;
    action: string;
  };
  noGuests: string;
  noGuestsSuffix: string;
  guestNamePlaceholder: string;
  guestNameRequired: string;
  contactPlaceholder: string;
  notesPlaceholder: string;
  locateBtn: string;
  upload: string;
  batchesSaved: string;
  batchSaved: string;
  uploadHint: string;
  uploadedNoRecords: string;
  uploadedRows: string;
  importGuests: string;
  downloadTemplateSample: string;
  uploadData: string;
  downloading: string;
  loadingUploadedRecords: string;
  uploading: string;
  uploadGuestExcelTitle: string;
  guestGroupLabel: string;
  brideGroup: string;
  groomGroup: string;
  excelFileLabel: string;
  chooseFile: string;
  noFileSelected: string;
  cancel: string;
  sessionExpiredSignInAgain: string;
  chooseExcelFileFirst: string;
  unableDownloadTemplate: string;
  uploadFailed: string;
  uploadSuccessSummary: string;
  sameNameButton: string;
  uploadedFilterTabs: {
    all: string;
    groom: string;
    bride: string;
  };
};

type RegistrySectionProps = {
  registryText: Record<Language, RegistryText>;
  language: Language;
  registryGuests: Guest[];
  registrySearch: string;
  onRegistrySearchChange: (value: string) => void;
  onLoadSameNameGuests: (gift?: SameNameGiftFilter) => void;
  sameNameGiftCounts: {
    withGift: number;
    noGift: number;
  };
  addGuestRow: () => void;
  handleDownloadRegistryExcel: () => void;
  uploadedGuests: Guest[];
  nameErrorIds: number[];
  setNameErrorIds: Dispatch<SetStateAction<number[]>>;
  updateGuest: (
    id: number,
    patch: Partial<Pick<Guest, "name" | "phone" | "category" | "status" | "notes" | "latitude" | "longitude">>,
  ) => void;
  categories: string[];
  categoryTranslations: Record<string, string>;
  statusSelectClass: (status: GuestStatus) => string;
  statusTextClass: (status: GuestStatus) => string;
  removeGuestRow: (id: number) => void;
  mapInstanceRef: MutableRefObject<unknown>;
  markerRef: MutableRefObject<unknown>;
  setMapGuestId: Dispatch<SetStateAction<number | null>>;
  setMapOpen: Dispatch<SetStateAction<boolean>>;
  uploadedBatches: Array<unknown>;
  handleUploadSnapshot: () => void;
  isUploadingDrafts: boolean;
  showUploadedTable: boolean;
  setShowUploadedTable: Dispatch<SetStateAction<boolean>>;
  uploadedGroupFilter: UploadedDraftGroupFilter;
  uploadedGuestCounts: {
    all: number;
    bride: number;
    groom: number;
  };
  uploadedGuestsPage: number;
  uploadedGuestsTotalPages: number;
  onUploadedGuestsPageChange: (page: number) => void;
  onUploadedFilterChange: (filter: UploadedDraftGroupFilter) => void;
  uploadError: string;
  isLoadingUploadedGuests: boolean;
  uploadedGuestsError: string;
  statusBadgeClass: (status: GuestStatus) => string;
  handleEditUploadedRow: (guest: Guest) => void;
  onEditSelectedUploadedRows: (guests: Guest[]) => void;
  onDeleteSelectedLocalRows: (ids: number[]) => void;
  setUploadedDeleteId: Dispatch<SetStateAction<number | null>>;
  onDeleteSelectedUploadedRows: (ids: number[]) => void;
  isDeletingSelectedUploadedRows: boolean;
  updateResult: { type: "success" | "error"; message: string } | null;
  onCloseUpdateResult: () => void;
};

function RegistrySection({
  registryText,
  language,
  registryGuests,
  registrySearch,
  onRegistrySearchChange,
  onLoadSameNameGuests,
  sameNameGiftCounts,
  addGuestRow,
  handleDownloadRegistryExcel,
  uploadedGuests,
  nameErrorIds,
  setNameErrorIds,
  updateGuest,
  categories,
  categoryTranslations,
  removeGuestRow,
  mapInstanceRef,
  markerRef,
  setMapGuestId,
  setMapOpen,
  uploadedBatches,
  handleUploadSnapshot,
  isUploadingDrafts,
  showUploadedTable,
  setShowUploadedTable,
  uploadedGroupFilter,
  uploadedGuestCounts,
  uploadedGuestsPage,
  uploadedGuestsTotalPages,
  onUploadedGuestsPageChange,
  onUploadedFilterChange,
  uploadError,
  isLoadingUploadedGuests,
  uploadedGuestsError,
  handleEditUploadedRow,
  onEditSelectedUploadedRows,
  onDeleteSelectedLocalRows,
  setUploadedDeleteId,
  onDeleteSelectedUploadedRows,
  isDeletingSelectedUploadedRows,
  updateResult,
  onCloseUpdateResult,
}: RegistrySectionProps) {
  const [showInputTable, setShowInputTable] = useState(false);
  const [showImportActions, setShowImportActions] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isUploadingImportFile, setIsUploadingImportFile] = useState(false);
  const [importGuestGroup, setImportGuestGroup] = useState<ImportGuestGroup>("bride");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [uploadedCategoryFilter, setUploadedCategoryFilter] = useState("all");
  const [selectedLocalIds, setSelectedLocalIds] = useState<number[]>([]);
  const [selectedUploadedIds, setSelectedUploadedIds] = useState<number[]>([]);
  const [isRegistrySearchFocused, setIsRegistrySearchFocused] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadedFilterTabs: Array<{ key: UploadedDraftGroupFilter; label: string; count: number }> = [
    {
      key: "all",
      label: registryText[language].uploadedFilterTabs.all,
      count: uploadedGuestCounts.all,
    },
    {
      key: "groom",
      label: registryText[language].uploadedFilterTabs.groom,
      count: uploadedGuestCounts.groom,
    },
    {
      key: "bride",
      label: registryText[language].uploadedFilterTabs.bride,
      count: uploadedGuestCounts.bride,
    },
  ];
  const uploadedFilterLabel =
    uploadedFilterTabs.find((tab) => tab.key === uploadedGroupFilter)?.label ??
    registryText[language].uploadedFilterTabs.all;
  const totalGuestsLabel = language === "kh" ? "ភ្ញៀវសរុប" : "Total guests";
  const sameNameWithGiftLabel = language === "kh" ? "ឈ្មោះដូចគ្នា មានចំណងដៃ" : "Same Name Have Gift";
  const sameNameNoGiftLabel = language === "kh" ? "ឈ្មោះដូចគ្នា អត់ចំណងដៃ" : "Same Name No Gift";
  const giftColumnLabel = language === "kh" ? "ចំណងដៃ" : "Gift";
  const groupColumnLabel = language === "kh" ? "ក្រុម" : "Group";
  const invitedColumnLabel = language === "kh" ? "បានអញ្ជើញ" : "Invited";
  const doneColumnLabel = language === "kh" ? "រួចរាល់" : "Done";
  const draftGroupLabel = (guest: Guest) =>
    guest.group === "ភ្ញៀវកូនស្រី"
      ? registryText[language].brideGroup
      : registryText[language].groomGroup;
  const booleanStatusLabel = (value?: boolean) => {
    if (language === "kh") {
      return value ? "បាទ/ចាស" : "ទេ";
    }
    return value ? "Yes" : "No";
  };
  const formatDraftDate = (value?: string | null) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString(language === "kh" ? "km-KH" : "en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const renderGiftStatus = (guest: Guest) => {
    const amount = guest.giftAmount ?? null;
    if (amount == null || amount <= 0) {
      return language === "kh" ? "អត់ចំណងដៃ" : "No gift";
    }
    if (guest.giftCurrency === "Riel") {
      return `${new Intl.NumberFormat("km-KH").format(amount)} ៛`;
    }
    return `$${amount}`;
  };
  const localizeCategory = (category: string) => {
    if (language !== "kh") return category;
    const normalized = category.trim().toLowerCase();
    const mapped = categoryTranslations[normalized];
    if (mapped?.trim()) return mapped.trim();
    if (normalized === "friend" || normalized === "friends") return "មិត្តភក្តិ";
    if (normalized === "colleague" || normalized === "colleagues") return "មិត្តរួមការងារ";
    if (normalized === "management") return "ថ្នាក់គ្រប់គ្រង";
    if (normalized === "relative" || normalized === "relatives") return "សាច់ញាតិ";
    if (normalized === "sibling" || normalized === "siblings") return "បងប្អូន";
    if (normalized === "friends of parents") return "មិត្តភក្តិរបស់ឪពុកម្តាយ";
    if (normalized === "sibling's friend" || normalized === "siblings friend") {
      return "មិត្តភក្តិបងប្អូន";
    }
    return category;
  };
  const uniqueCategories = Array.from(
    new Map(
      categories.map((category) => [category.trim().toLowerCase(), category]),
    ).values(),
  );
  const uploadedCategoryOptions = uniqueCategories.filter((category) => category.trim());
  const normalizedUploadedCategoryFilter = uploadedCategoryFilter.trim().toLowerCase();
  const filteredUploadedGuests =
    normalizedUploadedCategoryFilter === "all"
      ? uploadedGuests
      : uploadedGuests.filter(
          (guest) => guest.category.trim().toLowerCase() === normalizedUploadedCategoryFilter,
        );
  const shouldPaginateFilteredUploadedGuests =
    uploadedGuestsTotalPages <= 1 && filteredUploadedGuests.length > REGISTRY_ROWS_PER_PAGE;
  const effectiveUploadedGuestsTotalPages = shouldPaginateFilteredUploadedGuests
    ? Math.max(1, Math.ceil(filteredUploadedGuests.length / REGISTRY_ROWS_PER_PAGE))
    : uploadedGuestsTotalPages;
  const effectiveUploadedGuestsPage = Math.min(
    Math.max(1, uploadedGuestsPage),
    effectiveUploadedGuestsTotalPages,
  );
  const uploadedGuestsRowOffset = (effectiveUploadedGuestsPage - 1) * REGISTRY_ROWS_PER_PAGE;
  const paginatedUploadedGuests = shouldPaginateFilteredUploadedGuests
    ? filteredUploadedGuests.slice(
        (effectiveUploadedGuestsPage - 1) * REGISTRY_ROWS_PER_PAGE,
        effectiveUploadedGuestsPage * REGISTRY_ROWS_PER_PAGE,
      )
    : filteredUploadedGuests;
  const editableRegistryGuests = registryGuests.filter(
    (guest) => (guest.source ?? "local") === "local",
  );
  const shouldShowUploadedTable = !showInputTable || showUploadedTable;
  const isAllUploadedFilter = uploadedGroupFilter === "all";
  const selectedVisibleLocalIds = selectedLocalIds.filter((id) =>
    editableRegistryGuests.some((guest) => guest.id === id),
  );
  const selectedVisibleUploadedIds = selectedUploadedIds.filter((id) =>
    paginatedUploadedGuests.some((guest) => guest.id === id),
  );
  const allLocalSelected =
    editableRegistryGuests.length > 0 &&
    selectedVisibleLocalIds.length === editableRegistryGuests.length;
  const allUploadedSelected =
    paginatedUploadedGuests.length > 0 &&
    selectedVisibleUploadedIds.length === paginatedUploadedGuests.length;
  const hasSelectedUploadedRows = selectedVisibleUploadedIds.length > 0;
  const hasSelectedLocalRows = selectedVisibleLocalIds.length > 0;
  const canDeleteSelectedRows =
    hasSelectedUploadedRows || (hasSelectedLocalRows && !isDeletingSelectedUploadedRows);

  useEffect(() => {
    if (editableRegistryGuests.length > 0) {
      setShowInputTable(true);
      return;
    }
    setShowInputTable(false);
  }, [editableRegistryGuests.length]);

  useEffect(() => {
    setSelectedUploadedIds([]);
  }, [uploadedGuests, uploadedGroupFilter, registrySearch, uploadedCategoryFilter]);

  useEffect(() => {
    if (!shouldPaginateFilteredUploadedGuests) return;
    if (uploadedGuestsPage === effectiveUploadedGuestsPage) return;
    onUploadedGuestsPageChange(effectiveUploadedGuestsPage);
  }, [
    shouldPaginateFilteredUploadedGuests,
    uploadedGuestsPage,
    effectiveUploadedGuestsPage,
    onUploadedGuestsPageChange,
  ]);

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportError("");
    setImportSuccess("");
    setImportFile(null);
    setImportGuestGroup("bride");
  };

  const parseFileName = (contentDisposition: string | null) => {
    if (!contentDisposition) {
      return "wedding-guest-import-template.xlsx";
    }
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }
    return "wedding-guest-import-template.xlsx";
  };

  const handleDownloadImportTemplate = async (event?: ReactMouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setImportError(registryText[language].sessionExpiredSignInAgain);
      return;
    }

    setIsDownloadingTemplate(true);
    setImportError("");
    setShowImportActions(false);

    try {
      const response = await fetch(buildApiUrl(API_IMPORT_TEMPLATE_PATH), {
        method: "GET",
        cache: "no-store",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(registryText[language].unableDownloadTemplate);
      }

      const blob = await response.blob();
      const fileName = parseFileName(response.headers.get("content-disposition"));
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setImportError(
        error instanceof Error ? error.message : registryText[language].unableDownloadTemplate,
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const openImportUploadModal = () => {
    setShowImportActions(false);
    setImportError("");
    setImportSuccess("");
    setImportFile(null);
    setShowImportModal(true);
  };

  const handleUploadImportFile = async () => {
    if (!importFile) {
      setImportError(registryText[language].chooseExcelFileFirst);
      return;
    }

    const token = localStorage.getItem("wedding_access_token");
    if (!token) {
      setImportError(registryText[language].sessionExpiredSignInAgain);
      return;
    }

    setIsUploadingImportFile(true);
    setImportError("");
    setImportSuccess("");

    try {
      const formData = new FormData();
      formData.append("guest_group", importGuestGroup);
      formData.append("file", importFile);

      const response = await fetch(buildApiUrl(API_IMPORT_EXCEL_PATH), {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const payload = (await response.json().catch(() => null)) as
        | {
            guest_group?: string;
            created_count?: number;
            failed_count?: number;
            errors?: string[];
            message?: string;
          }
        | null;

      if (!response.ok) {
        throw new Error(payload?.message ?? registryText[language].uploadFailed);
      }

      setImportSuccess(
        registryText[language].uploadSuccessSummary
          .replace("{created}", String(payload?.created_count ?? 0))
          .replace("{failed}", String(payload?.failed_count ?? 0)),
      );
      setImportFile(null);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : registryText[language].uploadFailed);
    } finally {
      setIsUploadingImportFile(false);
    }
  };

  return (
    <>
      <div className="mt-2 min-h-[calc(100svh-4.75rem)] sm:mt-4 sm:h-full sm:min-h-0">
        <div className="relative flex h-full min-h-0 flex-col overflow-visible rounded-3xl border border-[color:var(--panel-border)] bg-[color:var(--card)] p-4 shadow-[var(--panel-shadow)] sm:overflow-hidden sm:p-7">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[color:var(--accent)]/8 to-transparent"
        />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-[family-name:var(--font-display)] text-xl text-[color:var(--foreground)] sm:text-2xl">
                {registryText[language].title}
              </h3>
              <div className="flex items-center gap-2 sm:hidden">
                <button
                  type="button"
                  onClick={() => {
                    if (isAllUploadedFilter) return;
                    setShowInputTable(true);
                    addGuestRow();
                  }}
                  disabled={isAllUploadedFilter}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-semibold transition ${
                    isAllUploadedFilter
                      ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                      : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
                  }`}
                  aria-label="Add guest"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={handleDownloadRegistryExcel}
                  data-tab-trigger="true"
                  disabled={uploadedGuests.length === 0}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition ${
                    uploadedGuests.length === 0
                      ? "cursor-not-allowed border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--muted)] opacity-60"
                      : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                  }`}
                  aria-label="Download Excel"
                  title="Download Excel"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 3v12" />
                    <path d="m7 10 5 5 5-5" />
                    <path d="M4 19h16" />
                  </svg>
                </button>
                <div className="relative">
                  <button
                    type="button"
                    data-tab-trigger="true"
                    onClick={() => {
                      setShowImportActions((prev) => !prev);
                      setImportError("");
                    }}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                    aria-label={registryText[language].importGuests}
                    title={registryText[language].importGuests}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 16V4" />
                      <path d="m7 9 5-5 5 5" />
                      <path d="M5 19h14" />
                    </svg>
                  </button>
                  {showImportActions ? (
                    <div className="absolute right-0 top-11 z-30 w-48 rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-1 shadow-lg">
                      <button
                        type="button"
                        data-tab-trigger="true"
                        onMouseDown={(event) => {
                          void handleDownloadImportTemplate(event);
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        disabled={isDownloadingTemplate}
                        className="flex w-full items-center justify-start rounded-lg px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isDownloadingTemplate
                          ? registryText[language].downloading
                          : registryText[language].downloadTemplateSample}
                      </button>
                      <button
                        type="button"
                        data-tab-trigger="true"
                        onClick={openImportUploadModal}
                        className="mt-1 flex w-full items-center justify-start rounded-lg px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                      >
                        {registryText[language].uploadData}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <p className="mt-1 text-sm text-[color:var(--muted)]">
              {editableRegistryGuests.length} {registryText[language].registered} |{" "}
              {uploadedGuestCounts.all} {totalGuestsLabel}
            </p>
          </div>
          <div className="w-full lg:w-auto">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="flex max-w-full flex-nowrap gap-1.5 overflow-x-auto rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] p-1.5">
                {uploadedFilterTabs.map((tab) => {
                  const isActive = uploadedGroupFilter === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      data-tab-trigger="true"
                      onClick={() => onUploadedFilterChange(tab.key)}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                        isActive
                          ? "bg-[color:var(--accent)] text-white"
                          : "text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                      }`}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  );
                })}
              </div>
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <div className="relative min-w-0 flex-1 sm:w-56">
                  <input
                    value={registrySearch}
                    onChange={(event) => onRegistrySearchChange(event.target.value)}
                    onFocus={() => setIsRegistrySearchFocused(true)}
                    onBlur={() => setIsRegistrySearchFocused(false)}
                    placeholder={registryText[language].searchPlaceholder}
                    className="h-10 w-full rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 pr-10 text-sm text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)]"
                  />
                  {registrySearch.trim() || isRegistrySearchFocused ? (
                    <button
                      type="button"
                      onClick={() => onRegistrySearchChange("")}
                      className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/80 bg-white/90 text-slate-600 transition hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:bg-slate-800"
                      aria-label="Clear search"
                      title="Clear search"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                <div className="relative shrink-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setUploadedCategoryFilter("all");
                        onLoadSameNameGuests("with_gift");
                      }}
                      className={`inline-flex h-9 items-center justify-center rounded-full px-3 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/30 ${
                        isLoadingUploadedGuests
                          ? "cursor-wait border border-[color:var(--field-border)] bg-[color:var(--surface-hover)] text-[color:var(--muted)] opacity-80"
                          : "bg-emerald-600 text-white hover:bg-emerald-700"
                      }`}
                      disabled={isLoadingUploadedGuests}
                      aria-label={sameNameWithGiftLabel}
                      title={sameNameWithGiftLabel}
                    >
                      {sameNameWithGiftLabel}
                      <span className="ml-2 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-[10px] font-bold leading-none text-white">
                        {sameNameGiftCounts.withGift > 99 ? "99+" : sameNameGiftCounts.withGift}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUploadedCategoryFilter("all");
                        onLoadSameNameGuests("no_gift");
                      }}
                      className={`inline-flex h-9 items-center justify-center rounded-full px-3 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/30 ${
                        isLoadingUploadedGuests
                          ? "cursor-wait border border-[color:var(--field-border)] bg-[color:var(--surface-hover)] text-[color:var(--muted)] opacity-80"
                          : "bg-amber-600 text-white hover:bg-amber-700"
                      }`}
                      disabled={isLoadingUploadedGuests}
                      aria-label={sameNameNoGiftLabel}
                      title={sameNameNoGiftLabel}
                    >
                      {sameNameNoGiftLabel}
                      <span className="ml-2 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-[10px] font-bold leading-none text-white">
                        {sameNameGiftCounts.noGift > 99 ? "99+" : sameNameGiftCounts.noGift}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <select
                value={uploadedCategoryFilter}
                onChange={(event) => setUploadedCategoryFilter(event.target.value)}
                className="h-10 w-full rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 text-xs font-medium text-[color:var(--foreground)] outline-none transition focus:border-[color:var(--accent)] sm:w-44"
              >
                <option value="all">
                  {language === "kh" ? "គ្រប់ប្រភេទ" : "All categories"}
                </option>
                {uploadedCategoryOptions.map((category) => (
                  <option key={`uploaded-category-${category.trim().toLowerCase()}`} value={category}>
                    {localizeCategory(category)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  if (isAllUploadedFilter) return;
                  setShowInputTable(true);
                  addGuestRow();
                }}
                disabled={isAllUploadedFilter}
                className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-semibold transition sm:flex ${
                  isAllUploadedFilter
                    ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                    : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
                }`}
                aria-label="Add guest"
              >
                +
              </button>
              <button
                type="button"
                onClick={handleDownloadRegistryExcel}
                data-tab-trigger="true"
                disabled={uploadedGuests.length === 0}
                className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border transition sm:flex ${
                  uploadedGuests.length === 0
                    ? "cursor-not-allowed border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--muted)] opacity-60"
                    : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                }`}
                aria-label="Download Excel"
                title="Download Excel"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3v12" />
                  <path d="m7 10 5 5 5-5" />
                  <path d="M4 19h16" />
                </svg>
              </button>
              <div className="relative hidden sm:block">
                <button
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => {
                    setShowImportActions((prev) => !prev);
                    setImportError("");
                  }}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                  aria-label={registryText[language].importGuests}
                  title={registryText[language].importGuests}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 16V4" />
                    <path d="m7 9 5-5 5 5" />
                    <path d="M5 19h14" />
                  </svg>
                </button>
                {showImportActions ? (
                  <div className="absolute right-0 top-11 z-30 w-48 rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-1 shadow-lg">
                    <button
                      type="button"
                      data-tab-trigger="true"
                      onMouseDown={(event) => {
                        void handleDownloadImportTemplate(event);
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      disabled={isDownloadingTemplate}
                      className="flex w-full items-center justify-start rounded-lg px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isDownloadingTemplate
                        ? registryText[language].downloading
                        : registryText[language].downloadTemplateSample}
                    </button>
                    <button
                      type="button"
                      data-tab-trigger="true"
                      onClick={openImportUploadModal}
                      className="mt-1 flex w-full items-center justify-start rounded-lg px-3 py-2 text-xs font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
                    >
                      {registryText[language].uploadData}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {showInputTable ? (
        <div className="mt-6 flex-1 min-h-0 overflow-hidden rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)]/40">
          <div className="max-h-[72svh] overflow-x-auto overflow-y-auto overscroll-contain pb-6 sm:h-full sm:max-h-none">
          <div className="hidden space-y-3 p-3 sm:hidden">
            {editableRegistryGuests.length === 0 ? (
              <div className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-5 text-center text-sm text-[color:var(--muted)]">
                {registryText[language].noGuests} {registryText[language].noGuestsSuffix}
              </div>
            ) : (
              editableRegistryGuests.map((guest, index) => (
                <div
                  key={guest.id}
                  className="space-y-3 rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-3 shadow-[0_8px_24px_rgba(2,6,23,0.06)]"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]">
                    #{index + 1}
                  </p>
                  <input
                    value={guest.name}
                    onChange={(event) => {
                      const nextName = event.target.value;
                      updateGuest(guest.id, { name: nextName });
                      if (nextName.trim()) {
                        setNameErrorIds((prev) => prev.filter((id) => id !== guest.id));
                      }
                    }}
                    onBlur={(event) => {
                      if (!event.target.value.trim()) {
                        setNameErrorIds((prev) => (prev.includes(guest.id) ? prev : [...prev, guest.id]));
                      }
                    }}
                    placeholder={
                      nameErrorIds.includes(guest.id)
                        ? registryText[language].guestNameRequired
                        : registryText[language].guestNamePlaceholder
                    }
                    className={`w-full rounded-lg border bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)] ${
                      nameErrorIds.includes(guest.id)
                        ? "border-red-500 placeholder:text-red-500"
                        : "border-[color:var(--field-border)] placeholder:text-[color:var(--muted)]"
                    }`}
                  />
                  <select
                    value={guest.category}
                    onChange={(event) => updateGuest(guest.id, { category: event.target.value })}
                    className="w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
                  >
                    {uniqueCategories.map((category) => (
                      <option key={category.trim().toLowerCase()} value={category}>
                        {localizeCategory(category)}
                      </option>
                    ))}
                  </select>
                  <input
                    value={guest.notes}
                    onChange={(event) => updateGuest(guest.id, { notes: event.target.value })}
                    placeholder={registryText[language].notesPlaceholder}
                    className="w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
                  />
                  <div className="grid grid-cols-2 gap-2 text-xs text-[color:var(--muted)]">
                    <span>Lat: {guest.latitude != null ? guest.latitude.toFixed(6) : "-"}</span>
                    <span>Lng: {guest.longitude != null ? guest.longitude.toFixed(6) : "-"}</span>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        mapInstanceRef.current = null;
                        markerRef.current = null;
                        setMapGuestId(guest.id);
                        setMapOpen(true);
                      }}
                      data-tab-trigger="true"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-sky-200 bg-sky-50/80 text-sky-700 shadow-sm transition duration-150 hover:bg-sky-100"
                      aria-label={registryText[language].locateBtn}
                      title={registryText[language].locateBtn}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 21s6-5.33 6-10a6 6 0 1 0-12 0c0 4.67 6 10 6 10Z" />
                        <circle cx="12" cy="11" r="2.5" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      data-tab-trigger="true"
                      onClick={() => removeGuestRow(guest.id)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-200 bg-red-50/80 text-red-600 shadow-sm transition duration-150 hover:bg-red-100"
                      aria-label="Clear row"
                      title="Clear row"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M3 6h18" />
                        <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                        <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                        <path d="M10 10.5v6" />
                        <path d="M14 10.5v6" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <table className="min-w-[1320px] w-full table-fixed text-left text-sm sm:min-w-[920px]">
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "44%" }} />
              <col style={{ width: "26%" }} />
              <col style={{ width: "28%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)] text-xs uppercase tracking-widest text-[color:var(--muted)]">
              <tr>
                <th className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={allLocalSelected}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedLocalIds(editableRegistryGuests.map((guest) => guest.id));
                        return;
                      }
                      setSelectedLocalIds([]);
                    }}
                    className="h-4 w-4 rounded border-[color:var(--field-border)]"
                    aria-label="Select all input rows"
                  />
                </th>
                <th className="px-5 py-3 text-center font-semibold sm:px-4">{registryText[language].table.no}</th>
                <th className="px-5 py-3 text-center font-semibold sm:px-4">{registryText[language].table.name}</th>
                <th className="px-5 py-3 text-center font-semibold sm:px-4">{registryText[language].table.category}</th>
                <th className="px-5 py-3 text-center font-semibold sm:px-4">{registryText[language].table.notes}</th>
                <th className="px-5 py-3 text-center font-semibold sm:px-4">{registryText[language].table.latitude}</th>
                <th className="px-5 py-3 text-center font-semibold sm:px-4">{registryText[language].table.longitude}</th>
                <th className="px-5 py-3 pr-10 text-center font-semibold sm:px-4 sm:pr-8">{registryText[language].table.locate}</th>
                <th className="px-5 py-3 pl-10 text-center font-semibold sm:px-4 sm:pl-8">
                  {registryText[language].table.action}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--divider)] bg-[color:var(--surface)]">
              {editableRegistryGuests.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                    {registryText[language].noGuests} {registryText[language].noGuestsSuffix}
                  </td>
                </tr>
              ) : (
                <>
                {editableRegistryGuests.map((guest, index) => (
                  <tr key={guest.id}>
                    <td className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={selectedLocalIds.includes(guest.id)}
                        onChange={(event) => {
                          setSelectedLocalIds((prev) =>
                            event.target.checked
                              ? prev.includes(guest.id)
                                ? prev
                                : [...prev, guest.id]
                              : prev.filter((id) => id !== guest.id),
                          );
                        }}
                        className="h-4 w-4 rounded border-[color:var(--field-border)]"
                        aria-label={`Select input row ${index + 1}`}
                      />
                    </td>
                    <td className="px-5 py-3 text-[color:var(--muted)] sm:px-4">{index + 1}</td>
                    <td className="relative px-5 py-3 sm:px-4">
                      <input
                        value={guest.name}
                        onChange={(event) => {
                          const nextName = event.target.value;
                          updateGuest(guest.id, { name: nextName });
                          if (nextName.trim()) {
                            setNameErrorIds((prev) => prev.filter((id) => id !== guest.id));
                          }
                        }}
                        onBlur={(event) => {
                          if (!event.target.value.trim()) {
                            setNameErrorIds((prev) =>
                              prev.includes(guest.id) ? prev : [...prev, guest.id],
                            );
                          }
                        }}
                        placeholder={
                          nameErrorIds.includes(guest.id)
                            ? registryText[language].guestNameRequired
                            : registryText[language].guestNamePlaceholder
                        }
                        className={`w-full rounded-lg border bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)] ${
                          nameErrorIds.includes(guest.id)
                            ? "border-red-500 placeholder:text-red-500"
                            : "border-[color:var(--field-border)] placeholder:text-[color:var(--muted)]"
                        }`}
                      />
                    </td>
                    <td className="px-5 py-3 sm:px-4">
                      <select
                        value={guest.category}
                        onChange={(event) =>
                          updateGuest(guest.id, {
                            category: event.target.value,
                          })
                        }
                        className="w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
                      >
                        {uniqueCategories.map((category) => (
                          <option key={category.trim().toLowerCase()} value={category}>
                            {localizeCategory(category)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3 pl-10 sm:px-4 sm:pl-8">
                      <input
                        value={guest.notes}
                        onChange={(event) => updateGuest(guest.id, { notes: event.target.value })}
                        placeholder={registryText[language].notesPlaceholder}
                        className="w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
                      />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-[color:var(--muted)] sm:px-4">
                      {guest.latitude ? guest.latitude.toFixed(6) : "-"}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-[color:var(--muted)] sm:px-4">
                      {guest.longitude ? guest.longitude.toFixed(6) : "-"}
                    </td>
                    <td className="px-5 py-3 sm:px-4">
                      <button
                        type="button"
                        onClick={() => {
                          mapInstanceRef.current = null;
                          markerRef.current = null;
                          setMapGuestId(guest.id);
                          setMapOpen(true);
                        }}
                        data-tab-trigger="true"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-sky-200 bg-sky-50/80 text-sky-700 shadow-sm transition duration-150 hover:-translate-y-0.5 hover:border-sky-300 hover:bg-sky-100 hover:shadow"
                        aria-label={registryText[language].locateBtn}
                        title={registryText[language].locateBtn}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 21s6-5.33 6-10a6 6 0 1 0-12 0c0 4.67 6 10 6 10Z" />
                          <circle cx="12" cy="11" r="2.5" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-5 py-3 sm:px-4">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          data-tab-trigger="true"
                          onClick={() => removeGuestRow(guest.id)}
                          className="group inline-flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-rose-100/80 text-red-600 shadow-sm transition duration-150 hover:-translate-y-0.5 hover:border-red-300 hover:from-red-100 hover:to-rose-200/80 hover:shadow"
                          aria-label="Clear row"
                          title="Clear row"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4 transition-transform duration-150 group-hover:scale-110"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M3 6h18" />
                            <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                            <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                            <path d="M10 10.5v6" />
                            <path d="M14 10.5v6" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                </>
              )}
            </tbody>
          </table>
          </div>
        </div>
        ) : null}
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)]/60 p-3 text-sm text-[color:var(--muted)] sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <span>
              {uploadedBatches.length > 0
              ? `${uploadedBatches.length} upload batch${
                  uploadedBatches.length === 1
                    ? ` ${registryText[language].batchSaved}`
                    : ` ${registryText[language].batchesSaved}`
                }`
              : registryText[language].uploadHint}
          </span>
          <span>
            {filteredUploadedGuests.length} {registryText[language].uploadedRows}
          </span>
          <div className="flex w-full items-center gap-2 self-start sm:w-auto sm:self-auto">
            <button
              type="button"
              onClick={() => {
                const selectedIdSet = new Set(selectedVisibleUploadedIds);
                const selectedGuests = paginatedUploadedGuests.filter((guest) =>
                  selectedIdSet.has(guest.id),
                );
                if (selectedGuests.length === 0) return;
                setShowInputTable(true);
                onEditSelectedUploadedRows(selectedGuests);
                setSelectedUploadedIds([]);
              }}
              data-tab-trigger="true"
              disabled={selectedVisibleUploadedIds.length === 0}
              className={`flex h-9 min-w-9 items-center justify-center rounded-full px-3 transition ${
                selectedVisibleUploadedIds.length === 0
                  ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                  : "bg-amber-600 text-white hover:bg-amber-700"
              }`}
              aria-label="Edit selected uploaded rows"
              title="Edit selected uploaded rows"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 21h6" />
                <path d="M14.5 4.5a2.12 2.12 0 0 1 3 3L8 17l-4 1 1-4 9.5-9.5Z" />
              </svg>
              <span className="ml-2 text-xs font-semibold">{selectedVisibleUploadedIds.length}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (hasSelectedUploadedRows) {
                  onDeleteSelectedUploadedRows(selectedVisibleUploadedIds);
                  return;
                }
                if (!hasSelectedLocalRows) return;
                onDeleteSelectedLocalRows(selectedVisibleLocalIds);
                setSelectedLocalIds([]);
              }}
              data-tab-trigger="true"
              disabled={!canDeleteSelectedRows}
              className={`flex h-9 min-w-9 items-center justify-center rounded-full px-3 transition ${
                !canDeleteSelectedRows
                  ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
              aria-label={hasSelectedUploadedRows ? "Delete selected uploaded rows" : "Delete selected input rows"}
              title={hasSelectedUploadedRows ? "Delete selected uploaded rows" : "Delete selected input rows"}
            >
              {isDeletingSelectedUploadedRows ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" aria-hidden="true">
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.3"
                    strokeWidth="2"
                  />
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                    <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                    <path d="M10 10.5v6" />
                    <path d="M14 10.5v6" />
                  </svg>
                  <span className="ml-2 text-xs font-semibold">
                    {hasSelectedUploadedRows ? selectedVisibleUploadedIds.length : selectedVisibleLocalIds.length}
                  </span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleUploadSnapshot}
              className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest transition ${
                editableRegistryGuests.length === 0 || isUploadingDrafts
                  ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                  : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
              }`}
              disabled={editableRegistryGuests.length === 0 || isUploadingDrafts}
            >
              {isUploadingDrafts ? registryText[language].uploading : registryText[language].upload}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!showInputTable) return;
                setShowUploadedTable((prev) => !prev);
              }}
              data-tab-trigger="true"
              className={`ml-auto flex h-9 w-9 items-center justify-center rounded-full border text-base transition sm:ml-0 ${
                shouldShowUploadedTable
                  ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-white"
                  : "border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
              }`}
              aria-label={shouldShowUploadedTable ? "Hide uploaded table" : "View uploaded table"}
              title={shouldShowUploadedTable ? "Hide uploaded table" : "View uploaded table"}
              disabled={!showInputTable}
            >
              {shouldShowUploadedTable ? (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
                  <circle cx="12" cy="12" r="3" />
                  <path d="M4 4l16 16" />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {uploadError ? <p className="mt-2 text-sm text-red-600">{uploadError}</p> : null}
        {shouldShowUploadedTable ? (
          <>
            <div
              className={`mt-6 min-h-0 overflow-hidden rounded-2xl border border-[color:var(--field-border)] ${
                showInputTable ? "" : "flex-1"
              }`}
            >
              <div
                className={`relative overflow-x-auto overflow-y-auto overscroll-contain pb-6 ${
                  showInputTable
                    ? "max-h-[72svh] sm:h-[clamp(12rem,30svh,22rem)] sm:max-h-none"
                    : "max-h-[78svh] sm:h-full sm:max-h-none"
                }`}
              >
                <div className="hidden space-y-3 p-3 sm:hidden">
                  {uploadedGuestsError ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
                      {uploadedGuestsError}
                    </div>
                  ) : paginatedUploadedGuests.length === 0 ? (
                    <div className="rounded-xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-5 text-center text-sm text-[color:var(--muted)]">
                      {isLoadingUploadedGuests
                        ? registryText[language].loadingUploadedRecords
                        : `${registryText[language].uploadedNoRecords} ${uploadedFilterLabel}.`}
                    </div>
                  ) : (
                    paginatedUploadedGuests.map((guest, index) => (
                      <div
                        key={guest.id}
                        className="space-y-3 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-4 shadow-[0_8px_24px_rgba(2,6,23,0.06)]"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-semibold text-[color:var(--foreground)]">
                            #{uploadedGuestsRowOffset + index + 1} {guest.name || "-"}
                          </p>
                        </div>
                        <p className="text-xs font-medium text-[color:var(--foreground)]">
                          {localizeCategory(guest.category)}
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-[color:var(--muted)]">
                          <span>{groupColumnLabel}: {draftGroupLabel(guest)}</span>
                          <span>{giftColumnLabel}: {renderGiftStatus(guest)}</span>
                          <span>{invitedColumnLabel}: {booleanStatusLabel(guest.invited)}</span>
                          <span>{doneColumnLabel}: {booleanStatusLabel(guest.done)}</span>
                        </div>
                        <p className="text-xs text-[color:var(--muted)]">{guest.notes || "-"}</p>
                        <div className="grid grid-cols-2 gap-2 rounded-xl bg-[color:var(--surface-strong)]/60 p-2 text-xs text-[color:var(--muted)]">
                          <span>Lat: {guest.latitude != null ? guest.latitude.toFixed(6) : "-"}</span>
                          <span>Lng: {guest.longitude != null ? guest.longitude.toFixed(6) : "-"}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <table
                  className={`min-w-[1460px] w-full table-fixed text-left text-sm transition-opacity duration-300 ${
                    isLoadingUploadedGuests ? "opacity-85" : "opacity-100"
                  }`}
                >
                  <colgroup>
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)] text-xs uppercase tracking-widest text-[color:var(--muted)]">
                    <tr>
                      <th className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={allUploadedSelected}
                          onChange={(event) => {
                            if (event.target.checked) {
                              setSelectedUploadedIds(paginatedUploadedGuests.map((guest) => guest.id));
                              return;
                            }
                            setSelectedUploadedIds([]);
                          }}
                          className="h-4 w-4 rounded border-[color:var(--field-border)]"
                          aria-label="Select all uploaded rows"
                        />
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">{registryText[language].table.no}</th>
                      <th className="px-4 py-3 text-center font-semibold">{registryText[language].table.name}</th>
                      <th className="px-4 py-3 text-center font-semibold">{groupColumnLabel}</th>
                      <th className="px-4 py-3 text-center font-semibold">{registryText[language].table.category}</th>
                      <th className="px-4 py-3 text-center font-semibold">{giftColumnLabel}</th>
                      <th className="px-4 py-3 text-center font-semibold">{invitedColumnLabel}</th>
                      <th className="px-4 py-3 text-center font-semibold">{doneColumnLabel}</th>
                      <th className="px-4 py-3 text-center font-semibold">{registryText[language].table.notes}</th>
                      <th className="px-4 py-3 text-center font-semibold">{registryText[language].table.latitude}</th>
                      <th className="px-4 py-3 text-center font-semibold">{registryText[language].table.longitude}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--divider)] bg-[color:var(--surface)]">
                    {uploadedGuestsError ? (
                      <tr>
                        <td colSpan={11} className="px-4 py-6 text-center text-sm text-red-600">
                          {uploadedGuestsError}
                        </td>
                      </tr>
                    ) : paginatedUploadedGuests.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-4 py-6 text-center text-sm text-[color:var(--muted)]">
                          {isLoadingUploadedGuests
                            ? registryText[language].loadingUploadedRecords
                            : `${registryText[language].uploadedNoRecords} ${uploadedFilterLabel}.`}
                        </td>
                      </tr>
                    ) : (
                      paginatedUploadedGuests.map((guest, index) => (
                        <tr key={guest.id} className="transition-colors hover:bg-[color:var(--surface-hover)]/65">
                          <td className="px-2 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedUploadedIds.includes(guest.id)}
                              onChange={(event) => {
                                setSelectedUploadedIds((prev) =>
                                  event.target.checked
                                    ? prev.includes(guest.id)
                                      ? prev
                                      : [...prev, guest.id]
                                    : prev.filter((id) => id !== guest.id),
                                );
                              }}
                              className="h-4 w-4 rounded border-[color:var(--field-border)]"
                              aria-label={`Select uploaded row ${index + 1}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-[color:var(--muted)]">
                            {uploadedGuestsRowOffset + index + 1}
                          </td>
                          <td className="px-4 py-3 truncate">{guest.name || "-"}</td>
                          <td className="px-4 py-3 truncate">{draftGroupLabel(guest)}</td>
                          <td className="px-4 py-3 truncate">{localizeCategory(guest.category)}</td>
                          <td className="px-4 py-3 truncate">{renderGiftStatus(guest)}</td>
                          <td className="px-4 py-3 truncate">{booleanStatusLabel(guest.invited)}</td>
                          <td className="px-4 py-3 truncate">{booleanStatusLabel(guest.done)}</td>
                          <td className="px-4 py-3 truncate">{guest.notes || "-"}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-[color:var(--muted)]">
                            {guest.latitude != null ? guest.latitude.toFixed(6) : "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[color:var(--muted)]">
                            {guest.longitude != null ? guest.longitude.toFixed(6) : "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {isLoadingUploadedGuests ? (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-[color:var(--surface)]/80">
                    <svg viewBox="0 0 24 24" className="h-10 w-10 animate-spin" aria-hidden="true">
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity="0.25"
                        strokeWidth="2"
                      />
                      <path
                        d="M21 12a9 9 0 0 0-9-9"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                ) : null}
              </div>
            </div>
            {effectiveUploadedGuestsTotalPages > 1 ? (
              <div className="mt-4 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-3 text-sm text-[color:var(--muted)]">
                <span>
                  {language === "kh"
                    ? `ទំព័រ ${effectiveUploadedGuestsPage} / ${effectiveUploadedGuestsTotalPages}`
                    : `Page ${effectiveUploadedGuestsPage} / ${effectiveUploadedGuestsTotalPages}`}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-tab-trigger="true"
                    onClick={() => onUploadedGuestsPageChange(Math.max(1, effectiveUploadedGuestsPage - 1))}
                    disabled={effectiveUploadedGuestsPage <= 1 || isLoadingUploadedGuests}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      effectiveUploadedGuestsPage <= 1 || isLoadingUploadedGuests
                        ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                        : "border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                    }`}
                  >
                    {language === "kh" ? "មុន" : "Prev"}
                  </button>
                  <button
                    type="button"
                    data-tab-trigger="true"
                    onClick={() => onUploadedGuestsPageChange(Math.min(effectiveUploadedGuestsTotalPages, effectiveUploadedGuestsPage + 1))}
                    disabled={effectiveUploadedGuestsPage >= effectiveUploadedGuestsTotalPages || isLoadingUploadedGuests}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      effectiveUploadedGuestsPage >= effectiveUploadedGuestsTotalPages || isLoadingUploadedGuests
                        ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                        : "border border-[color:var(--field-border)] bg-[color:var(--surface)] text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                    }`}
                  >
                    {language === "kh" ? "បន្ទាប់" : "Next"}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        </div>
      </div>
      {updateResult ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "var(--modal-backdrop)" }}
        >
          <div className="w-full max-w-sm rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-6 shadow-[var(--modal-shadow)]">
            <h3
              className={`text-lg font-semibold ${
                updateResult.type === "success" ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {updateResult.type === "success" ? "Success" : "Failed"}
            </h3>
            <p className="mt-2 text-sm text-[color:var(--muted)]">{updateResult.message}</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={onCloseUpdateResult}
                className="rounded-full bg-[color:var(--accent)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white transition hover:brightness-110"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showImportModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "var(--modal-backdrop)" }}
          onClick={closeImportModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-[color:var(--field-border)] bg-[color:var(--surface)] p-6 shadow-[var(--modal-shadow)]"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[color:var(--foreground)]">
              {registryText[language].uploadGuestExcelTitle}
            </h3>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)]">
              {registryText[language].guestGroupLabel}
            </label>
            <select
              value={importGuestGroup}
              onChange={(event) => setImportGuestGroup(event.target.value as ImportGuestGroup)}
              className="mt-2 w-full rounded-lg border border-[color:var(--field-border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--foreground)] outline-none focus:border-[color:var(--accent)]"
            >
              <option value="bride">{registryText[language].brideGroup}</option>
              <option value="groom">{registryText[language].groomGroup}</option>
            </select>

            <label className="mt-4 block text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)]">
              {registryText[language].excelFileLabel}
            </label>
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setImportFile(file);
                setImportError("");
              }}
              className="hidden"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={() => importFileInputRef.current?.click()}
                className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
              >
                {registryText[language].chooseFile}
              </button>
              <p className="truncate text-xs text-[color:var(--muted)]">
                {importFile?.name ?? registryText[language].noFileSelected}
              </p>
            </div>

            {importError ? <p className="mt-3 text-sm text-red-600">{importError}</p> : null}
            {importSuccess ? <p className="mt-3 text-sm text-emerald-600">{importSuccess}</p> : null}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                data-tab-trigger="true"
                onClick={closeImportModal}
                className="rounded-full border border-[color:var(--field-border)] bg-[color:var(--surface)] px-4 py-2 text-xs font-semibold uppercase tracking-widest text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
              >
                {registryText[language].cancel}
              </button>
              <button
                type="button"
                data-tab-trigger="true"
                onClick={handleUploadImportFile}
                disabled={isUploadingImportFile}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest transition ${
                  isUploadingImportFile
                    ? "cursor-not-allowed bg-[color:var(--field-border)] text-[color:var(--muted)] opacity-70"
                    : "bg-[color:var(--accent)] !text-white hover:brightness-110 dark:!text-white"
                }`}
              >
                {isUploadingImportFile ? registryText[language].uploading : registryText[language].upload}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

const areRegistryPropsEqual = (
  prev: Readonly<RegistrySectionProps>,
  next: Readonly<RegistrySectionProps>,
) => {
  return (
    prev.registryText === next.registryText &&
    prev.language === next.language &&
    prev.registryGuests === next.registryGuests &&
    prev.registrySearch === next.registrySearch &&
    prev.uploadedGuests === next.uploadedGuests &&
    prev.nameErrorIds === next.nameErrorIds &&
    prev.categories === next.categories &&
    prev.categoryTranslations === next.categoryTranslations &&
    prev.uploadedBatches === next.uploadedBatches &&
    prev.isUploadingDrafts === next.isUploadingDrafts &&
    prev.showUploadedTable === next.showUploadedTable &&
    prev.uploadedGroupFilter === next.uploadedGroupFilter &&
    prev.uploadedGuestCounts === next.uploadedGuestCounts &&
    prev.uploadError === next.uploadError &&
    prev.isLoadingUploadedGuests === next.isLoadingUploadedGuests &&
    prev.uploadedGuestsError === next.uploadedGuestsError &&
    prev.isDeletingSelectedUploadedRows === next.isDeletingSelectedUploadedRows &&
    prev.updateResult === next.updateResult
  );
};

export default memo(RegistrySection, areRegistryPropsEqual);
